/**
 * How each kind of question is drawn — the other half of the widget registry.
 *
 * The rules half is `lib/audition/widgets.ts`, and it has no DOM in it on
 * purpose: `lib/audition/steps.ts` is imported by the single serverless
 * function that serves every tRPC route, so a registry holding a React
 * component could not live there. Both are a `Record` over the same field-type
 * union, so a new widget is a compile error in each half until both are
 * written, which is the whole point of the split.
 *
 * The props are the rules half's `StepContext` plus the three things only a
 * drawn widget has. One type, so a widget cannot be settled against one set
 * of step facts and drawn against another.
 */
import { useMemo } from 'react'
import { ChipGroup } from '@/components/ui/chip-group'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { asList, asText, optionsOf } from '@/lib/audition/answer-shape'
import { isPriorityValue } from '@/lib/audition/job-priorities'
import { settleTimeline, timelineMonths } from '@/lib/audition/job-timeline'
import { rungsLevel } from '@/lib/audition/level-ladder'
import {
  OTHER_OPTION,
  OTHER_TEXT_MAX_LENGTH,
} from '@/lib/audition/other-answer'
import type { AuditionAnswer } from '@/lib/audition/steps'
import type { AuditionFieldType, StepContext } from '@/lib/audition/widgets'
import { storesList } from '@/lib/audition/widgets'
import { JobPriorities } from './job-priorities'
import { JobTimeline } from './job-timeline'
import { LevelLadder } from './level-ladder'

export type WidgetViewProps = StepContext & {
  value: AuditionAnswer | undefined
  disabled: boolean
  /**
   * The id of this question's help text, when it has any.
   *
   * On the props rather than left to the step, because only the widget knows
   * WHAT to hang it on: a single control takes it itself, a group takes it on
   * the group. Help text that is merely nearby on screen is not read out at
   * all by a screen reader — it has to be pointed at.
   */
  describedBy: string | undefined
  /**
   * Whether this question is the reason Next was refused.
   *
   * Only ever true after a submit has actually been attempted, so nobody is
   * told they got something wrong before they have touched it.
   */
  invalid: boolean
  onChange: (value: AuditionAnswer) => void
  /**
   * The Other text stored beside this answer, for a question that declares
   * `other`, and undefined for every other question. Passed apart from
   * `value` because it is a second stored key, not part of this answer's
   * shape.
   */
  otherText: { value: string; onChange: (value: string) => void } | undefined
}

export interface WidgetView {
  Component: (props: WidgetViewProps) => React.ReactNode
  /**
   * Whether the question's heading can be a real `<label for>`.
   *
   * False for every widget that is a group rather than a single focusable
   * control — there is nothing to point `htmlFor` at, so the heading is plain
   * text and the widget carries the accessible name on its own group.
   */
  labelledControl: boolean
}

export const WIDGET_VIEWS: Record<AuditionFieldType, WidgetView> = {
  text: {
    Component: ({ field, value, disabled, describedBy, invalid, onChange }) => (
      <Input
        id={field.name}
        placeholder={field.placeholder}
        value={asText(value)}
        disabled={disabled}
        aria-describedby={describedBy}
        aria-invalid={invalid || undefined}
        onChange={(e) => onChange(e.target.value)}
      />
    ),
    labelledControl: true,
  },
  textarea: {
    Component: ({ field, value, disabled, describedBy, invalid, onChange }) => (
      <Textarea
        id={field.name}
        rows={4}
        placeholder={field.placeholder}
        value={asText(value)}
        disabled={disabled}
        aria-describedby={describedBy}
        aria-invalid={invalid || undefined}
        onChange={(e) => onChange(e.target.value)}
      />
    ),
    labelledControl: true,
  },
  chips: {
    // `optionsOf` and not `field.options ?? []`: the union makes `options`
    // required on a chips field, so the fallback was dead by construction and
    // read as though an optionless chip group were a thing that happens. It is
    // not — and where the two halves used to disagree about that impossible
    // state (the rules half throwing by name, the view quietly drawing zero
    // chips for a question nobody can then answer), they now say the same
    // thing through the same function.
    Component: ({
      field,
      value,
      disabled,
      describedBy,
      invalid,
      onChange,
      otherText,
    }) => {
      const other =
        field.other && otherText
          ? {
              option: OTHER_OPTION,
              label: field.other.label,
              placeholder: field.other.placeholder,
              maxLength: OTHER_TEXT_MAX_LENGTH,
              ...otherText,
            }
          : undefined
      return storesList(field) ? (
        <ChipGroup
          multi
          label={field.label}
          options={optionsOf(field)}
          value={asList(value)}
          onChange={onChange}
          disabled={disabled}
          describedBy={describedBy}
          invalid={invalid}
          other={other}
        />
      ) : (
        <ChipGroup
          label={field.label}
          options={optionsOf(field)}
          value={asText(value)}
          onChange={onChange}
          disabled={disabled}
          describedBy={describedBy}
          invalid={invalid}
          other={other}
        />
      )
    },
    labelledControl: false,
  },
  ladder: {
    Component: ({
      field,
      value,
      answers,
      disabled,
      describedBy,
      invalid,
      onChange,
    }) => (
      <LevelLadder
        label={field.label}
        currentLevel={rungsLevel(field, answers)}
        value={asList(value)}
        onChange={onChange}
        disabled={disabled}
        describedBy={describedBy}
        invalid={invalid}
      />
    ),
    labelledControl: false,
  },
  timeline: {
    Component: ({
      field,
      value,
      today,
      disabled,
      describedBy,
      invalid,
      onChange,
    }) => {
      // The line `ready` judges this answer against too; see
      // `StepContext.today`. Memoized for identity across renders, not for
      // correctness — a dropped cache recomputes the line it already had.
      const months = useMemo(() => timelineMonths(today), [today])
      return (
        <JobTimeline
          label={field.label}
          months={months}
          // Through the read settling uses, so this draws what settling would
          // have produced even when handed an answer nobody settled. A stretch
          // stored badly is left off rather than blanking the other two, and
          // the chip string this question used to store — still shown wherever
          // the application is read back — opens the line empty.
          value={settleTimeline(value)}
          onChange={onChange}
          disabled={disabled}
          describedBy={describedBy}
          invalid={invalid}
        />
      )
    },
    labelledControl: false,
  },
  priorities: {
    Component: ({ field, value, disabled, describedBy, invalid, onChange }) => (
      <JobPriorities
        label={field.label}
        // Anything that is not a ranking — a draft from before this question
        // existed, or junk — reads as nothing said. The widget draws the board
        // we hand over from that; the stored answer stays empty until they
        // actually move a card.
        value={isPriorityValue(value) ? value : { ranked: [], dropped: [] }}
        onChange={onChange}
        disabled={disabled}
        describedBy={describedBy}
        invalid={invalid}
      />
    ),
    labelledControl: false,
  },
}
